import { HttpResponse } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AtlasLanguageService } from '@galaxy/atlas/core';
import {
  CreateCrmObjectRequestInterface,
  CreateCrmObjectResponseInterface,
  CrmObjectInterface,
  DeleteCrmObjectRequestInterface,
  GetMultiCrmObjectRequestInterface,
  GetMultiCrmObjectResponseInterface,
  ListCrmObjectsRequestInterface,
  ListCrmObjectsResponseInterface,
  ListFieldOptionsRequestInterface,
  ListFiltersRequestInterface,
  ListFiltersResponseInterface,
  ObjectProjectionFilterInterface,
  SortByInterface,
  SortDirection,
  StartBulkCompanyCreateRequestInterface,
  UpdateCrmObjectRequestInterface,
  ValidateCrmObjectRequestInterface,
  ValidateCrmObjectResponseInterface,
  CRMFieldSchemaApiService,
  CRMMultiLocationFieldSchemaApiService,
  ListFieldSchemaResponseInterface,
  GetMultiFieldSchemaResponseInterface,
  PagedRequestOptionsInterface,
} from '@vendasta/crm';
import { combineLatest, filter, map, Observable, of, shareReplay, switchMap, take } from 'rxjs';
import { listFieldSchema$ } from '@vendasta/protos/crm/v1/crmfield-schema-service';
import { toLegacyListFieldSchemaResponse } from '@galaxy/crm/services/adapter-service';
import { AbstractCrmObjectService } from './_abstract.service';
import { ContactService } from './_contact.service';
import { CompanyService } from './_company.service';
import { GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import { FilterGroupInterface } from '@vendasta/crm';
import { ActivityObjectService } from './_activity_object.service';
import { MatSort } from '@angular/material/sort';
import {
  CrmDependencies,
  CrmInjectionToken,
  CrmMultiLocationDependencies,
  CrmMultilocationInjectionToken,
  FieldValueInterface,
  MultiLocationContext,
  ObjectType,
} from '../../../tokens-and-interfaces';
import { convertGalaxyFilters } from '../../../utils';
import { SystemFieldIds } from '@galaxy/crm/services/field-service';
import { OpportunityService } from './_opportunity.service';
import { MultiLocationActivityService } from './multi-location-activity.service';
import { CustomObjectService } from './_custom_object.service';
import { MultiLocationContactService } from './multi-location-contact.service';
import { PotentialContactService } from './_potential-contact.service';
import { TicketService } from './_ticket.service';
import {
  stripPotentialContactSuffix,
  suffixPotentialContactFieldIds,
  suffixPotentialContactMultiFieldIds,
} from '../potential-contact-field-suffix';

@Injectable()
export class CrmObjectService {
  private handledObjectTypes: ObjectType[] = [
    'Company',
    'Contact',
    'Activity',
    'Opportunity',
    'CustomObject',
    'PotentialContact',
    'Ticket',
  ];
  private fieldOptionsRequestMap = new Map<string, Observable<string[]>>();
  isMultiLocationPage$: Observable<boolean>;
  private readonly languageService = inject(AtlasLanguageService);
  private locale = toSignal(this.languageService.language$);
  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    @Inject(CrmMultilocationInjectionToken) private readonly multiLocationConfig: CrmMultiLocationDependencies,
    private readonly contactService: ContactService,
    private readonly companyService: CompanyService,
    private readonly activityService: ActivityObjectService,
    private readonly opportunityService: OpportunityService,
    private readonly multiLocationActivityService: MultiLocationActivityService,
    private readonly multiLocationContactService: MultiLocationContactService,
    private readonly customObjectService: CustomObjectService,
    private readonly potentialContactService: PotentialContactService,
    private readonly ticketService: TicketService,
    private readonly crmFieldSchemaApiService: CRMFieldSchemaApiService,
    private readonly crmMultiLocationFieldSchemaService: CRMMultiLocationFieldSchemaApiService,
  ) {
    this.isMultiLocationPage$ = multiLocationConfig.isMultiLocation$
      ? combineLatest([multiLocationConfig.isMultiLocation$, config.namespace$]).pipe(
          map(([isMultiLocation, namespace]) => {
            if (namespace !== '') {
              return false;
            }
            return isMultiLocation;
          }),
        )
      : of(false);
  }

  private generateFieldOptionsRequestKey(
    objectType: ObjectType,
    request: ListFieldOptionsRequestInterface,
    namespace: string,
    isMultiLocation: boolean,
  ): string {
    const requestStr = JSON.stringify(request);
    return `${objectType}::${requestStr}::${namespace}::${isMultiLocation}`;
  }

  createCrmObject(objectType: ObjectType, crmObject: CrmObjectInterface): Observable<CreateCrmObjectResponseInterface> {
    if (crmObject.fields !== undefined) {
      crmObject.fields = this.setSystemFields(crmObject, crmObject.fields as FieldValueInterface[]);
    }

    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace: string) => {
        const request: CreateCrmObjectRequestInterface = {
          namespace: namespace,
          crmObject: crmObject,
        };
        return this.selectService(objectType).createCrmObject(request);
      }),
      take(1),
    );
  }

  deleteCrmObject(namespace: string, objectType: ObjectType, objectId: string): Observable<HttpResponse<null>> {
    const request: DeleteCrmObjectRequestInterface = {
      namespace: namespace,
      crmObjectId: objectId,
    };
    return this.selectService(objectType).deleteCrmObject(request).pipe(take(1));
  }

  validateCrmObject(
    objectType: ObjectType,
    crmObject: CrmObjectInterface,
  ): Observable<ValidateCrmObjectResponseInterface> {
    const crmObjectCopy = { ...crmObject };
    if (crmObjectCopy.fields !== undefined) {
      crmObjectCopy.fields = this.setSystemFields(crmObjectCopy, crmObjectCopy.fields as FieldValueInterface[]);
    }
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace: string) => {
        const request: ValidateCrmObjectRequestInterface = {
          namespace: namespace,
          crmObject: crmObjectCopy,
        };
        return this.selectService(objectType).validateCrmObject(request);
      }),
      take(1),
    );
  }

  private setSystemFields(crmObject: CrmObjectInterface, fieldValues: FieldValueInterface[]): FieldValueInterface[] {
    const contactOwnerID = fieldValues.find((field) => field.fieldId === SystemFieldIds.ContactOwnerID);
    if (contactOwnerID) {
      crmObject.ownerId = contactOwnerID.stringValue;
      fieldValues = fieldValues.filter((field) => field.fieldId !== SystemFieldIds.ContactOwnerID);
    }
    const contactGroupID = fieldValues.find((field) => field.fieldId === SystemFieldIds.ContactGroupID);
    if (contactGroupID) {
      crmObject.groupId = contactGroupID.stringValue;
      fieldValues = fieldValues.filter((field) => field.fieldId !== SystemFieldIds.ContactGroupID);
    }
    const contactExternalID = fieldValues.find((field) => field.fieldId === SystemFieldIds.ContactExternalID);
    if (contactExternalID) {
      crmObject.externalId = contactExternalID.stringValue;
      fieldValues = fieldValues.filter((field) => field.fieldId !== SystemFieldIds.ContactExternalID);
    }
    const companyOwnerID = fieldValues.find((field) => field.fieldId === SystemFieldIds.CompanyOwnerID);
    if (companyOwnerID) {
      crmObject.ownerId = companyOwnerID.stringValue;
      fieldValues = fieldValues.filter((field) => field.fieldId !== SystemFieldIds.CompanyOwnerID);
    }
    const companyGroupID = fieldValues.find((field) => field.fieldId === SystemFieldIds.CompanyGroupID);
    if (companyGroupID) {
      crmObject.groupId = companyGroupID.stringValue;
      fieldValues = fieldValues.filter((field) => field.fieldId !== SystemFieldIds.CompanyGroupID);
    }
    const companyExternalID = fieldValues.find((field) => field.fieldId === SystemFieldIds.CompanyExternalID);
    if (companyExternalID) {
      crmObject.externalId = companyExternalID.stringValue;
      fieldValues = fieldValues.filter((field) => field.fieldId !== SystemFieldIds.CompanyExternalID);
    }
    const opportunityOwnerID = fieldValues.find((field) => field.fieldId === SystemFieldIds.OpportunityOwnerID);
    if (opportunityOwnerID) {
      crmObject.ownerId = opportunityOwnerID.stringValue;
      fieldValues = fieldValues.filter((field) => field.fieldId !== SystemFieldIds.OpportunityOwnerID);
    }
    const customObjectOwnerID = fieldValues.find((field) => field.fieldId === SystemFieldIds.CustomObjectOwnerID);
    if (customObjectOwnerID) {
      crmObject.ownerId = customObjectOwnerID.stringValue;
      fieldValues = fieldValues.filter((field) => field.fieldId !== SystemFieldIds.CustomObjectOwnerID);
    }
    const customObjectExternalID = fieldValues.find((field) => field.fieldId === SystemFieldIds.CustomObjectExternalID);
    if (customObjectExternalID) {
      crmObject.externalId = customObjectExternalID.stringValue;
      fieldValues = fieldValues.filter((field) => field.fieldId !== SystemFieldIds.CustomObjectExternalID);
    }
    return fieldValues;
  }

  updateCrmObject(objectType: ObjectType, crmObject: CrmObjectInterface): Observable<HttpResponse<null>> {
    if (crmObject.fields !== undefined) {
      crmObject.fields = this.setSystemFields(crmObject, crmObject.fields as FieldValueInterface[]);
    }
    const paths: string[] = [];
    crmObject.fields?.forEach((field) => {
      paths.push(`fields.${field.fieldId}`);
    });
    if (crmObject.ownerId) {
      paths.push('OwnerID');
    }
    if (crmObject.groupId) {
      paths.push('GroupID');
    }
    if (crmObject.externalId) {
      paths.push('ExternalId');
    }

    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace: string) => {
        const request: UpdateCrmObjectRequestInterface = {
          namespace: namespace,
          crmObject: crmObject,
          fieldMask: {
            paths: paths,
          },
        };
        return this.selectService(objectType).updateCrmObject(request);
      }),
      take(1),
    );
  }

  convertSortBy(sorting: MatSort[] | undefined): SortByInterface[] | undefined {
    if (!sorting) {
      return undefined;
    }
    const sortBy: SortByInterface[] = [];

    for (const sort of sorting) {
      if (sort.direction === '') {
        return;
      }
      if (sort.direction === 'asc') {
        sortBy.push({
          fieldId: sort.active,
          sortDirection: SortDirection.SORT_DIRECTION_ASCENDING,
        });
      }
      if (sort.direction === 'desc') {
        sortBy.push({
          fieldId: sort.active,
          sortDirection: SortDirection.SORT_DIRECTION_DESCENDING,
        });
      }
    }
    return sortBy;
  }

  convertFilters(filters: GalaxyFilterInterface[] | undefined): FilterGroupInterface | undefined {
    return convertGalaxyFilters(filters);
  }

  listObjects(
    objectType: ObjectType,
    request: ListCrmObjectsRequestInterface,
    withNamespace?: string,
  ): Observable<ListCrmObjectsResponseInterface> {
    return combineLatest([this.config.namespace$, this.isMultiLocationPage$]).pipe(
      switchMap(([namespace, isMultiLocation]) => {
        if (withNamespace) {
          return this.selectService(objectType, false)
            .listObjects({ ...request, namespace: withNamespace })
            .pipe(take(1));
        }
        return this.selectService(objectType, isMultiLocation)
          .listObjects({ ...request, namespace: namespace })
          .pipe(take(1));
      }),
    );
  }

  listObjectsFilters(
    objectType: ObjectType,
    request: ListFiltersRequestInterface,
    withNamespace?: string,
  ): Observable<ListFiltersResponseInterface> {
    return combineLatest([this.config.namespace$, this.isMultiLocationPage$]).pipe(
      switchMap(([namespace, isMultiLocation]) => {
        if (withNamespace) {
          return this.selectService(objectType, false)
            .listObjectsFilters({ ...request, namespace: withNamespace })
            .pipe(take(1));
        }
        return this.selectService(objectType, isMultiLocation)
          .listObjectsFilters({ ...request, namespace: namespace })
          .pipe(take(1));
      }),
    );
  }

  getMultiObject(
    objectType: ObjectType,
    crmObjectIds: string[],
    projectionFilter: ObjectProjectionFilterInterface[] = [],
    withNamespaceOverride?: string,
  ): Observable<GetMultiCrmObjectResponseInterface> {
    return this.config.namespace$.pipe(
      filter((namespace) => !!namespace),
      take(1),
      switchMap((configNamespace) => {
        const namespace = withNamespaceOverride || configNamespace;
        const request: GetMultiCrmObjectRequestInterface = {
          namespace: namespace,
          crmObjectIds: crmObjectIds,
          projectionFilters: projectionFilter,
        };
        return this.selectService(objectType).getMultiObject(request);
      }),
    );
  }

  listFieldOptions(objectType: ObjectType, request: ListFieldOptionsRequestInterface): Observable<string[]> {
    const requestWithDefaults = {
      pageSize: 10,
      ...request,
    };

    return combineLatest([this.config.namespace$, this.isMultiLocationPage$]).pipe(
      switchMap(([namespace, isMultiLocation]) => {
        const requestKey = this.generateFieldOptionsRequestKey(
          objectType,
          requestWithDefaults,
          namespace,
          isMultiLocation,
        );

        if (this.fieldOptionsRequestMap.has(requestKey)) {
          return this.fieldOptionsRequestMap.get(requestKey)!;
        }

        const fieldOptions$ = this.selectService(objectType, isMultiLocation)
          .listFieldOptions({ ...requestWithDefaults, namespace: namespace })
          .pipe(take(1), shareReplay({ bufferSize: 1, refCount: true }));

        this.fieldOptionsRequestMap.set(requestKey, fieldOptions$);
        return fieldOptions$;
      }),
    );
  }

  private selectService(objectType: ObjectType, isMultiLocation?: boolean): AbstractCrmObjectService {
    if (!this.handledObjectTypes.includes(objectType)) {
      console.error('Object Type unhandled. The CRM Object Service will be undefined');
    }
    switch (objectType) {
      case 'Contact':
        if (isMultiLocation) {
          return this.multiLocationContactService;
        }
        return this.contactService;
      case 'Company':
        return this.companyService;
      case 'Activity':
        if (isMultiLocation) {
          return this.multiLocationActivityService;
        }
        return this.activityService;
      case 'Opportunity':
        return this.opportunityService;
      case 'CustomObject':
        return this.customObjectService;
      case 'PotentialContact':
        return this.potentialContactService;
      case 'Ticket':
        return this.ticketService;
      default:
        // Fallback to contact service for unhandled object types
        return this.contactService;
    }
  }

  startBulkCreate(objectType: ObjectType, searchTerm: string, crmObjects: CrmObjectInterface[]): Observable<string> {
    if (objectType !== 'Company') {
      console.error('Bulk create only supported for Company');
      throw new Error('Bulk create only supported for Company');
    }

    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        crmObjects.forEach((crmObject) => {
          crmObject.fields = this.setSystemFields(crmObject, crmObject.fields as FieldValueInterface[]);
        });
        const request: StartBulkCompanyCreateRequestInterface = {
          namespace: namespace,
          searchTerm: searchTerm,
          crmObjects: crmObjects,
        };
        return this.companyService.startBulkCreate(request);
      }),
      map((response) => response?.filterTag || ''),
      take(1),
    );
  }

  /**
   * Centralized method for listing field schemas with automatic locale inclusion.
   * This method handles namespace and multi-location logic internally.
   */
  listFieldSchemas(
    crmObjectType: string,
    crmObjectSubtype?: string,
    pagingOptions?: PagedRequestOptionsInterface,
    searchTerm?: string,
    filters?: any,
  ): Observable<ListFieldSchemaResponseInterface> {
    if (crmObjectType === 'Ticket') {
      return this.ticketService.listFieldSchemas(crmObjectSubtype);
    }
    const isPotentialContact = crmObjectType === 'PotentialContact';
    const lookupType = isPotentialContact ? 'Contact' : crmObjectType;
    return combineLatest([this.config.namespace$, this.isMultiLocationPage$]).pipe(
      take(1),
      switchMap(([namespace, isMultiLocation]) => {
        const request = {
          namespace,
          crmObjectType: lookupType,
          crmObjectSubtype: crmObjectSubtype || '',
          pagingOptions,
          searchTerm,
          filters,
          locale: this.locale(),
        };
        const isMultiLocationObject = lookupType === 'Contact' || lookupType === 'Activity';
        // Use multi-location service if applicable (for Contact objects in multi-location context)
        let response$: Observable<ListFieldSchemaResponseInterface>;
        if (
          isMultiLocation &&
          namespace === '' &&
          isMultiLocationObject &&
          this.multiLocationConfig.multiLocationContext$
        ) {
          response$ = (this.multiLocationConfig.multiLocationContext$ ?? of({} as MultiLocationContext)).pipe(
            take(1),
            switchMap((context) => {
              if (context?.accountGroupsIds?.length) {
                return this.crmMultiLocationFieldSchemaService.listMultiLocationFieldSchema({
                  namespaces: context.accountGroupsIds,
                  crmObjectType: lookupType,
                  crmObjectSubtype: crmObjectSubtype || '',
                  pagingOptions,
                  locale: this.locale(),
                });
              }
              return listFieldSchema$(request).pipe(map(toLegacyListFieldSchemaResponse));
            }),
          );
        } else {
          response$ = listFieldSchema$(request).pipe(map(toLegacyListFieldSchemaResponse));
        }

        if (isPotentialContact) {
          return response$.pipe(map((resp) => suffixPotentialContactFieldIds(resp)));
        }
        return response$;
      }),
    );
  }

  /**
   * Centralized method for getting multiple field schemas with automatic locale inclusion.
   * This method handles namespace logic internally.
   */
  getMultiFieldSchema(
    crmObjectType: string,
    fieldIds: string[],
    crmObjectSubtype?: string,
  ): Observable<GetMultiFieldSchemaResponseInterface> {
    if (crmObjectType === 'Ticket') {
      return this.ticketService.getMultiFieldSchema(fieldIds, crmObjectSubtype);
    }
    const isPotentialContact = crmObjectType === 'PotentialContact';
    const lookupType = isPotentialContact ? 'Contact' : crmObjectType;
    const lookupFieldIds = isPotentialContact ? fieldIds.map((id) => stripPotentialContactSuffix(id)) : fieldIds;
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace) => {
        const request = {
          namespace,
          crmObjectType: lookupType,
          crmObjectSubtype: crmObjectSubtype || '',
          fieldId: lookupFieldIds,
          locale: this.locale(),
        };
        const response$ = this.crmFieldSchemaApiService.getMultiFieldSchema(request);
        if (isPotentialContact) {
          return response$.pipe(map((resp) => suffixPotentialContactMultiFieldIds(resp)));
        }
        return response$;
      }),
    );
  }
}
